import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';

type Locale = 'en' | 'el';

type Metric = {
  value: string;
  label: string;
};

type SectionIntro = {
  eyebrow: string;
  heading: string;
  description: string;
};

type HeroCopy = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
  card: {
    eyebrow: string;
    title: string;
    description: string;
    bullets: string[];
  };
};

type Pillar = {
  title: string;
  description: string;
};

type BudgetExample = {
  revenue: string;
  conservativeValue: string;
  aggressiveValue: string;
};

type BudgetCopy = {
  intro: SectionIntro;
  labels: {
    revenue: string;
    conservative: string;
    aggressive: string;
  };
  examples: BudgetExample[];
  tip: {
    title: string;
    description: string;
  };
};

type ClosingCopy = {
  intro: SectionIntro;
  bullets: string[];
  note: string;
};

type CtaCopy = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
};

type PageCopy = {
  hero: HeroCopy;
  metrics: Metric[];
  costBreakdown: {
    intro: SectionIntro;
    pillars: Pillar[];
  };
  budget: BudgetCopy;
  roi: {
    intro: SectionIntro;
    cards: Pillar[];
  };
  performance: {
    intro: SectionIntro;
    kpis: Pillar[];
  };
  closing: ClosingCopy;
  cta: CtaCopy;
};

const copy: Record<Locale, PageCopy> = {
  en: {
    hero: {
      eyebrow: 'Marketing Cost Guide',
      title: 'Understand what marketing really costs.',
      description:
        'The question “How much does marketing cost?” does not have a simple answer. It depends on your business type, goals, competition, and strategy. This guide breaks down what you pay for, which costs are investments, and how to evaluate performance.',
      primaryCta: 'Book a free consultation',
      secondaryCta: 'See marketing services',
      card: {
        eyebrow: 'Inside the guide',
        title: 'What you will learn',
        description: 'A practical overview before you invest in campaigns.',
        bullets: [
          'What marketing cost includes',
          'Budget ranges and real examples',
          'Channels with stronger ROI',
          'KPIs that show real progress',
        ],
      },
    },
    metrics: [
      {
        value: '5–15% of revenue',
        label: 'Common annual budget range',
      },
      {
        value: 'Multi-channel mix',
        label: 'SEO, ads, content, social, email',
      },
      {
        value: 'Performance first',
        label: 'Measured by KPIs, not likes',
      },
    ],
    costBreakdown: {
      intro: {
        eyebrow: 'Cost components',
        heading: 'What marketing cost includes',
        description:
          "Marketing isn't a single line item. It combines strategy, execution, and continuous improvement across multiple channels.",
      },
      pillars: [
        {
          title: 'Website & landing pages',
          description: 'Creation or optimization of pages that convert visitors into leads.',
        },
        {
          title: 'SEO (Search Engine Optimization)',
          description: 'Improve organic visibility and demand in the long run.',
        },
        {
          title: 'Google & Meta Ads',
          description: 'Paid campaigns with cost control, targeting, and measurable ROI.',
        },
        {
          title: 'Social media & content',
          description: 'Planning, creation, and management of content that builds trust.',
        },
        {
          title: 'Email or SMS campaigns',
          description: 'Retention, reactivation, and promotion campaigns to existing audiences.',
        },
        {
          title: 'Analytics & optimization',
          description: 'Reporting, insights, and improvements based on data.',
        },
      ],
    },
    budget: {
      intro: {
        eyebrow: 'Budget planning',
        heading: 'How to set a realistic budget',
        description:
          'A common industry guideline is 5% to 15% of annual revenue — depending on whether the business is in growth, stability, or expansion mode.',
      },
      labels: {
        revenue: 'Annual revenue',
        conservative: 'Conservative (5%)',
        aggressive: 'Aggressive (15%)',
      },
      examples: [
        {
          revenue: '€100,000',
          conservativeValue: '€5,000/year • €420/month',
          aggressiveValue: '€15,000/year • €1,250/month',
        },
        {
          revenue: '€250,000',
          conservativeValue: '€12,500/year',
          aggressiveValue: '€37,500/year',
        },
      ],
      tip: {
        title: 'Tip:',
        description:
          'Businesses that view marketing as an expense remain stagnant. Those who treat it as an investment grow steadily with more predictable returns.',
      },
    },
    roi: {
      intro: {
        eyebrow: 'ROI focus',
        heading: 'Which services typically deliver the best ROI',
        description: 'ROI varies by business, but these channels often perform strongly when executed well.',
      },
      cards: [
        {
          title: 'SEO',
          description: 'Brings organic traffic and reduces the long-term need for paid ads.',
        },
        {
          title: 'Google Ads',
          description: 'Immediate visibility that is useful for new businesses or seasonal pushes.',
        },
        {
          title: 'Blog & content',
          description: 'Builds trust, supports SEO, and brings repeat traffic.',
        },
        {
          title: 'Social media',
          description: 'Boosts engagement and keeps your brand top of mind.',
        },
      ],
    },
    performance: {
      intro: {
        eyebrow: 'Measurement',
        heading: 'How to evaluate performance',
        description: 'No cost has value without measurement. Define clear KPIs for each goal.',
      },
      kpis: [
        {
          title: 'Increase traffic',
          description: 'Visits / CTR / SEO ranking',
        },
        {
          title: 'Increase leads',
          description: 'Forms / calls / requests',
        },
        {
          title: 'Sales',
          description: 'ROAS / Conversion Rate',
        },
        {
          title: 'Branding',
          description: 'Social engagement / Mentions / Reach',
        },
      ],
    },
    closing: {
      intro: {
        eyebrow: 'Final thought',
        heading: 'You pay for growth, not for “likes”',
        description: 'A smart marketing strategy should:',
      },
      bullets: [
        'Bring genuine interest',
        'Improve visitor-to-customer conversion',
        'Differentiate you from competition',
        'Be based on data, not assumptions',
      ],
      note:
        "If all of the above align with the services you're paying for, then you're not spending money — you're investing strategically.",
    },
    cta: {
      eyebrow: 'Next step',
      title: 'Have questions about marketing costs?',
      description: 'Book a free consultation and we will help you understand what you truly need.',
      primaryCta: 'Free consultation',
      secondaryCta: 'See marketing services',
    },
  },
  el: {
    hero: {
      eyebrow: 'ΟΔΗΓΟΣ ΚΟΣΤΟΥΣ MARKETING',
      title: 'Κατανοήστε το κόστος marketing πριν επενδύσετε.',
      description:
        'Η ερώτηση «Πόσο κοστίζει το marketing;» δεν έχει μία απλή απάντηση. Εξαρτάται από τον τύπο της επιχείρησης, τους στόχους, τον ανταγωνισμό και τη στρατηγική. Παρακάτω αναλύουμε τι πληρώνετε, ποια κόστη είναι επένδυση και πώς αξιολογείτε την απόδοση.',
      primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
      secondaryCta: 'Δείτε τις υπηρεσίες marketing',
      card: {
        eyebrow: 'ΤΙ ΠΕΡΙΛΑΜΒΑΝΕΙ',
        title: 'Τα βασικά σημεία',
        description: 'Μια πρακτική εικόνα πριν επενδύσετε σε καμπάνιες.',
        bullets: [
          'Τι περιλαμβάνει το κόστος marketing',
          'Εύρη κόστους και πραγματικά παραδείγματα',
          'Κανάλια με καλύτερο ROI',
          'KPIs που δείχνουν πρόοδο',
        ],
      },
    },
    metrics: [
      {
        value: '5–15% του τζίρου',
        label: 'Συνηθισμένο ετήσιο εύρος budget',
      },
      {
        value: 'Συνδυασμός καναλιών',
        label: 'SEO, ads, περιεχόμενο, social, email',
      },
      {
        value: 'Μετρήσιμη απόδοση',
        label: 'KPIs ανά στόχο, όχι απλώς «likes»',
      },
    ],
    costBreakdown: {
      intro: {
        eyebrow: 'ΤΙ ΠΛΗΡΩΝΕΤΕ',
        heading: 'Τι περιλαμβάνει το κόστος marketing',
        description:
          'Το marketing δεν είναι ένα μεμονωμένο έξοδο. Είναι συνδυασμός στρατηγικής, υλοποίησης και συνεχούς βελτίωσης.',
      },
      pillars: [
        {
          title: 'Ιστοσελίδα & landing pages',
          description: 'Κατασκευή ή βελτιστοποίηση σελίδων που μετατρέπουν επισκέπτες σε leads.',
        },
        {
          title: 'SEO (Βελτιστοποίηση Αναζήτησης)',
          description: 'Ενίσχυση οργανικής παρουσίας και μακροπρόθεσμη ζήτηση.',
        },
        {
          title: 'Google & Meta Ads',
          description: 'Πληρωμένες καμπάνιες με έλεγχο κόστους, στόχευση και απόδοση.',
        },
        {
          title: 'Social media & περιεχόμενο',
          description: 'Πλάνο, δημιουργία και διαχείριση περιεχομένου που χτίζει εμπιστοσύνη.',
        },
        {
          title: 'Email ή SMS καμπάνιες',
          description: 'Καμπάνιες διατήρησης, επαναδραστηριοποίησης και προώθησης.',
        },
        {
          title: 'Ανάλυση & βελτιστοποίηση',
          description: 'Reporting, συμπεράσματα και βελτιώσεις με βάση τα δεδομένα.',
        },
      ],
    },
    budget: {
      intro: {
        eyebrow: 'BUDGET',
        heading: 'Πώς καθορίζεται ένα ρεαλιστικό budget',
        description:
          'Ένας βασικός κανόνας του κλάδου είναι 5% έως 15% του ετήσιου κύκλου εργασιών, ανάλογα με το στάδιο ανάπτυξης της επιχείρησης.',
      },
      labels: {
        revenue: 'Ετήσιος τζίρος',
        conservative: 'Συντηρητικό (5%)',
        aggressive: 'Επιθετικό (15%)',
      },
      examples: [
        {
          revenue: '€100.000',
          conservativeValue: '€5.000/έτος • €420/μήνα',
          aggressiveValue: '€15.000/έτος • €1.250/μήνα',
        },
        {
          revenue: '€250.000',
          conservativeValue: '€12.500/έτος',
          aggressiveValue: '€37.500/έτος',
        },
      ],
      tip: {
        title: 'Tip:',
        description:
          'Οι επιχειρήσεις που βλέπουν το marketing ως έξοδο μένουν στάσιμες. Όσες το αντιμετωπίζουν ως επένδυση, αναπτύσσονται πιο σταθερά και με προβλέψιμη απόδοση.',
      },
    },
    roi: {
      intro: {
        eyebrow: 'ROI',
        heading: 'Ποιες υπηρεσίες έχουν συνήθως το καλύτερο ROI',
        description: 'Το ROI διαφέρει από επιχείρηση σε επιχείρηση, αλλά αυτά τα κανάλια συνήθως αποδίδουν καλύτερα.',
      },
      cards: [
        {
          title: 'SEO',
          description: 'Φέρνει οργανική επισκεψιμότητα και μειώνει την ανάγκη για πληρωμένες διαφημίσεις.',
        },
        {
          title: 'Google Ads',
          description: 'Δίνει άμεσα αποτελέσματα, χρήσιμο για νέες επιχειρήσεις ή εποχιακές καμπάνιες.',
        },
        {
          title: 'Blog & περιεχόμενο',
          description: 'Χτίζει εμπιστοσύνη, ενισχύει το SEO και φέρνει επαναλαμβανόμενη επισκεψιμότητα.',
        },
        {
          title: 'Social media',
          description: 'Αυξάνει την εμπλοκή και κρατά την επιχείρηση στο μυαλό του κοινού.',
        },
      ],
    },
    performance: {
      intro: {
        eyebrow: 'ΜΕΤΡΗΣΗ',
        heading: 'Πώς αξιολογείτε την απόδοση',
        description: 'Κανένα κόστος δεν έχει αξία χωρίς μέτρηση. Ορίστε ξεκάθαρα KPIs για κάθε στόχο.',
      },
      kpis: [
        {
          title: 'Αύξηση επισκεψιμότητας',
          description: 'Επισκέψεις / CTR / SEO ranking',
        },
        {
          title: 'Αύξηση leads',
          description: 'Φόρμες / τηλέφωνα / αιτήματα',
        },
        {
          title: 'Πωλήσεις',
          description: 'ROAS / Conversion Rate',
        },
        {
          title: 'Branding',
          description: 'Social Engagement / Mentions / Reach',
        },
      ],
    },
    closing: {
      intro: {
        eyebrow: 'ΤΕΛΙΚΗ ΣΚΕΨΗ',
        heading: 'Πληρώνετε για ανάπτυξη, όχι για «likes»',
        description: 'Μια έξυπνη στρατηγική marketing πρέπει:',
      },
      bullets: [
        'Να φέρνει πραγματικό ενδιαφέρον',
        'Να ενισχύει τη μετατροπή επισκεπτών σε πελάτες',
        'Να σας ξεχωρίζει από τον ανταγωνισμό',
        'Να βασίζεται σε στοιχεία, όχι υποθέσεις',
      ],
      note:
        'Εάν όλα τα παραπάνω ευθυγραμμίζονται με τις υπηρεσίες που πληρώνετε, τότε δεν ξοδεύετε χρήματα — επενδύετε στρατηγικά.',
    },
    cta: {
      eyebrow: 'ΕΠΟΜΕΝΟ ΒΗΜΑ',
      title: 'Έχετε ερωτήσεις για το κόστος marketing;',
      description: 'Κλείστε μια δωρεάν συμβουλευτική και θα σας βοηθήσουμε να καταλάβετε τι χρειάζεστε.',
      primaryCta: 'Δωρεάν συμβουλευτική',
      secondaryCta: 'Δείτε τις υπηρεσίες marketing',
    },
  },
};

export default function MarketingCostPage() {
  const { language } = useLanguage();
  const locale: Locale = language === 'en' ? 'en' : 'el';
  const localizedPath = useLocalizedPath();
  const location = useLocation();
  const meta = getPageMeta('marketing-cost', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const page = copy[locale];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <PixelBackground />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header variant="simple" />

        <main id="main-content" className="flex-1">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />

            <Container className="relative">
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-center">
                <div className="space-y-8">
                  <div className="space-y-5">
                    <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                      {page.hero.eyebrow}
                    </p>
                    <Display
                      variant="h1"
                      as="h1"
                      className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                    >
                      {page.hero.title}
                    </Display>
                  </div>

                  <Display variant="subhead" className="max-w-2xl">
                    {page.hero.description}
                  </Display>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      trackCta={{ text: page.hero.primaryCta, section: 'marketing_cost_hero', variant: 'primary' }}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center whitespace-nowrap sm:w-auto px-10 py-4 text-lg"
                    >
                      {page.hero.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/services/marketing-strategy')}
                      trackCta={{ text: page.hero.secondaryCta, section: 'marketing_cost_hero', variant: 'secondary' }}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center whitespace-nowrap sm:w-auto px-10 py-4 text-lg"
                    >
                      {page.hero.secondaryCta}
                    </Button>
                  </div>
                </div>

                <div className="relative mt-4 sm:mt-6 lg:mt-0">
                  <div className="pointer-events-none absolute -top-10 -right-14 h-32 w-32 rounded-full bg-brand-highlight/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-12 -left-16 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative w-full rounded-3xl border border-white/15 bg-black/70 p-6 shadow-glass backdrop-blur-xl sm:max-w-md sm:p-7 lg:ml-auto lg:p-9">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-brand-highlight/70">
                      {page.hero.card.eyebrow}
                    </p>
                    <h2 className="mt-3 text-lg font-semibold text-white sm:text-xl md:text-2xl">
                      {page.hero.card.title}
                    </h2>
                    <p className="mt-3 text-sm text-white/70">
                      {page.hero.card.description}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {page.hero.card.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {page.metrics.map((metric) => (
                  <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold tracking-tight text-white md:text-base">{metric.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{metric.label}</p>
                  </div>
                ))}
              </div>
            </Container>
          </HeroSection>

          <Section className="py-12 lg:py-16">
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-brand-highlight/70">
                  {page.costBreakdown.intro.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-4xl">
                  {page.costBreakdown.intro.heading}
                </h2>
                <p className="text-lg text-white/70">
                  {page.costBreakdown.intro.description}
                </p>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {page.costBreakdown.pillars.map((pillar) => (
                  <Card key={pillar.title} className="rounded-3xl border-white/15 bg-black/70 p-6 shadow-glass transition-all duration-300 hover:-translate-y-1">
                    <h3 className="text-lg font-semibold text-white">{pillar.title}</h3>
                    <p className="text-sm text-white/70">{pillar.description}</p>
                  </Card>
                ))}
              </div>
            </Container>
          </Section>

          <Section className="relative overflow-hidden">
            <div className="absolute inset-0 geometric-pattern opacity-40" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-brand-highlight/70">
                  {page.budget.intro.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-4xl">
                  {page.budget.intro.heading}
                </h2>
                <p className="text-lg text-white/70">
                  {page.budget.intro.description}
                </p>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-2">
                {page.budget.examples.map((example) => (
                  <Card key={example.revenue} className="rounded-3xl border-white/15 bg-black/70 p-6 shadow-glass transition-all duration-300 hover:-translate-y-1">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-white/55">
                      {page.budget.labels.revenue}
                    </p>
                    <p className="mt-3 text-2xl font-semibold text-white">{example.revenue}</p>
                    <div className="mt-6 space-y-3">
                      <div className="flex items-start justify-between gap-4 text-sm">
                        <span className="text-white/70">{page.budget.labels.conservative}</span>
                        <span className="font-semibold text-white">{example.conservativeValue}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4 text-sm">
                        <span className="text-white/70">{page.budget.labels.aggressive}</span>
                        <span className="font-semibold text-white">{example.aggressiveValue}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="mt-8 rounded-3xl border border-brand-highlight/40 bg-brand-highlight/10 p-6">
                <p className="text-sm text-white">
                  <span className="font-semibold text-white">{page.budget.tip.title}</span>{' '}
                  {page.budget.tip.description}
                </p>
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-brand-highlight/70">
                  {page.roi.intro.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-4xl">
                  {page.roi.intro.heading}
                </h2>
                <p className="text-lg text-white/70">{page.roi.intro.description}</p>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-2">
                {page.roi.cards.map((card) => (
                  <Card key={card.title} className="rounded-3xl border-white/15 bg-black/70 p-6 shadow-glass transition-all duration-300 hover:-translate-y-1">
                    <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                    <p className="text-sm text-white/70">{card.description}</p>
                  </Card>
                ))}
              </div>
            </Container>
          </Section>

          <Section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-brand-highlight/70">
                  {page.performance.intro.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-4xl">
                  {page.performance.intro.heading}
                </h2>
                <p className="text-lg text-white/70">{page.performance.intro.description}</p>
              </div>

              <div className="mt-10 grid gap-6 md:grid-cols-2">
                {page.performance.kpis.map((kpi) => (
                  <Card key={kpi.title} className="rounded-3xl border-white/15 bg-black/70 p-6 shadow-glass transition-all duration-300 hover:-translate-y-1">
                    <p className="text-sm font-semibold text-white">{kpi.title}</p>
                    <p className="mt-2 text-sm text-white/70">{kpi.description}</p>
                  </Card>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/60 px-8 py-6 text-center">
                  <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-brand-highlight/70">
                    {page.closing.intro.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {page.closing.intro.heading}
                  </Display>
                </div>

                <p className="mx-auto max-w-3xl text-center text-lg text-white/70">
                  {page.closing.intro.description}
                </p>

                <ul className="mt-8 grid gap-3 text-left text-sm text-white/70 sm:grid-cols-2 lg:grid-cols-4">
                  {page.closing.bullets.map((bullet) => (
                    <li
                      key={bullet}
                      className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                    >
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                      <span className="leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 rounded-2xl border border-brand-highlight/30 bg-brand-highlight/10 p-6 text-center">
                  <p className="text-sm text-white/80">{page.closing.note}</p>
                </div>
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-10 text-center shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative">
                  <p className="text-[11px] font-medium uppercase tracking-[0.35em] text-brand-highlight/70">
                    {page.cta.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-3xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {page.cta.title}
                  </Display>
                  <p className="mx-auto mt-4 max-w-2xl text-center text-lg text-white/70">
                    {page.cta.description}
                  </p>
                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      trackCta={{ text: page.cta.primaryCta, section: 'marketing_cost_cta', variant: 'primary' }}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {page.cta.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/services/marketing-strategy')}
                      trackCta={{ text: page.cta.secondaryCta, section: 'marketing_cost_cta', variant: 'secondary' }}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {page.cta.secondaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </div>
  );
}
